import { IApplicationData } from 'types/applications';

import HeroImage from 'assets/applications/first/hero.webp';
import FirstBlockImage from 'assets/applications/app-1.webp';

import DozenBoatsImage from 'assets/customer-cases/brunswick-use-case/dozen-boats.webp';
import ThreeBoatsImage from 'assets/customer-cases/brunswick-use-case/three-boats.webp';
import FiveBoatsImage from 'assets/customer-cases/brunswick-use-case/five-boats.webp';
import CityViewImage from 'assets/customer-cases/brunswick-use-case/city-view.webp';

import AutomationIcon from 'assets/applications/icons/automation.svg';
import DeveloperModeIcon from 'assets/applications/icons/developer-mode.svg';

import BrainIcon from 'assets/applications/icons/why-choose-us/brain.svg';
import TickBoxIcon from 'assets/applications/icons/why-choose-us/tick-box.svg';
import ScaleUpIcon from 'assets/applications/icons/why-choose-us/scale-up.svg';
import CircleAroundIcon from 'assets/applications/icons/why-choose-us/circle-around.svg';
import ThomasKImage from 'assets/avatars/ThomasK.svg';
import MichaelGrantImage from 'assets/avatars/MichaelGrant.svg';
import { brunswickUseCaseCustomerCaseMeta } from 'constants/pages';

export const firstApplicationData: IApplicationData = {
  hero: {
    title: 'Optimizing the volume of tests with simulation in the loop',
    description: `
      Experience smarter, faster, and safer validation for autonomous systems. 
      Our intelligent simulation technology transforms testing into a more efficient and reliable process.
    `,
    videoSrc: 'https://ailivesim.com/Videos/AILiveSim_Navy.mp4',
    imageSrc: HeroImage,
  },
  firstTextImageData: {
    title: 'Simulation-in-the-Loop for Autonomous System Testing',
    description: `
      As autonomous systems become more advanced, simulation is critical for safe, effective validation. 
      Yet many current testing methods are still manual, slow, and leave gaps in coverage. Trust in AI systems doesn’t come easily ; 
      it is built through rigorous, repeatable validation. AILiveSim transforms your approach with intelligent, high-coverage simulation 
      that runs locally or in the cloud. Automate complex test scenarios, uncover hidden weaknesses, and deliver explainable results — 
      so you can build AI people trust.
    `,
    imageSrc: FirstBlockImage,
    listItems: [
      'Manual recreation and annotation of complex scenarios',
      'Inconsistent test coverage, limiting AI model reliability',
      'Difficulty integrating simulation into fast-paced CI/CD pipelines',
    ],
  },
  informationData: {
    title: 'Smarter, Scalable AI Testing with AiLS',
    description: `
      AiLS  revolutionizes the simulation process by automating complex testing tasks. This
      intelligent platform enables faster development cycles while ensuring robust validation.
      With its advanced capabilities, teams can confidently deploy their AI solutions.
    `,
    items: [
      {
        title: 'AI-Powered Test Automation to Minimize Manual Effort and Errors',
        description: 'Automate testing processes to enhance efficiency and reduce human error.',
        imageSrc: AutomationIcon,
      },
      {
        title: 'Continuous Evaluation for Early Issue Detection and Rapid Iteration',
        description: 'Identify potential issues early to streamline your development process.',
        imageSrc: DeveloperModeIcon,
      },
    ],
    listItems: [
      "Cloud or on-premise simulation: scalable to your team's needs",
      'Intelligent test automation with AI-powered workflows',
      'Early issue detection through continuous performance evaluation',
      'Seamless integration into CI/CD for rapid iteration',
    ],
    listTitle: 'Key Capabilities of AiLS  for Enhanced Testing Efficiency',
  },
  whyChooseUsData: {
    title: 'Why Teams Choose\nAILiveSim for Enhanced\nTesting Efficiency',
    blueBoxTitle: `
      Make simulation an efficient, automated part of your AI testing strategy! With AiLS ,
      innovation teams validate faster, scale smarter, and deploy with confidence.
    `,
    items: [
      {
        title: 'Reduce Manual Effort & Human Error',
        imageSrc: BrainIcon,
        description:
          'Streamline testing with intelligent automation that minimizes repetitive tasks and reduces the risk of human mistakes.',
      },
      {
        description:
          'Achieve more reliable and explainable AI by expanding your test coverage across diverse real-world scenarios.',
        imageSrc: TickBoxIcon,
        title: 'Improve Coverage & AI Performance',
      },
      {
        imageSrc: ScaleUpIcon,
        title: 'Scale Seamlessly, Anywhere',
        description:
          'Run thousands of simulations on demand — whether in the cloud or on-premise — with full flexibility.',
      },
      {
        imageSrc: CircleAroundIcon,
        title: 'Accelerate Development Cycles',
        description:
          'Integrate simulation into your CI/CD pipeline for faster iteration, earlier bug detection, and quicker releases.',
      },
    ],
  },
  secondTextImageData: {
    title: 'Customer Case: Develop & Test AI Auto-Docking for Leisure Boats',
    description: `
      Discover how our client leveraged scenario-based simulation to enhance the performance and reliability
      of an advanced auto-docking system. This innovative approach not only streamlined operations
      but also ensured safety in challenging maritime environments.
    `,
    images: {
      firstImage: {
        imageSrc: DozenBoatsImage,
        altText: 'Dozen boats in the water near the shore',
      },
      secondImage: {
        imageSrc: ThreeBoatsImage,
        altText: 'Three boats in the water near the shore',
      },
      thirdImage: {
        imageSrc: FiveBoatsImage,
        altText: 'Five boats in the water near the shore',
      },
      fourthImage: {
        imageSrc: CityViewImage,
        altText: 'City view with boats in the water',
      },
    },
    link: brunswickUseCaseCustomerCaseMeta.url,
  },
  carouselData: [
    {
      authorImage: MichaelGrantImage,
      authorName: 'Michael Grant',
      authorRole: 'Innovation Lead at a major maritime OEM',
      text: '“Simulation allowed us to iterate quickly, validate safely, and deliver an intelligent co-captain experience the market hadn’t seen before.”',
    },
    {
      authorImage: ThomasKImage,
      authorName: 'Thomas K.',
      authorRole: 'Program Lead, Helicopter OEM',
      text: `"AILiveSim allowed us to simulate the unpredictable—from night-time flights near power lines to low-visibility urban environments. It's the only solution that offered the
        variability and realism our project demanded."`,
    },
  ],
};
